/* eslint-disable no-nested-ternary */
import styled, { css } from 'styled-components';
import StyledMaterialIcon from '../Icon/styles';
import { focusVisibleWithOutline } from '../utils/focus-indicator';
import { tertiary } from './emphasis';
import * as Sizes from './sizes';
const getButtonStyles = ({
  theme,
  $emphasis,
  $destructive,
  $isFilter,
  disabled
}) => {
  const {
    actionablePrimary,
    contentReverse,
    actionableHover,
    semanticError,
    semanticErrorBorderHover,
    backgroundPrimary,
    border: borderColor,
    borderHover,
    borderStrong,
    borderStrongHover,
    semanticInfoStrong,
    content,
    actionableSecondary
  } = theme.palette;

  // All colors have [nonDestructive, destructive] values defined
  const colors = {
    primary: [{
      background: actionablePrimary,
      border: actionablePrimary,
      text: contentReverse,
      hover: {
        background: actionableHover,
        border: actionableHover,
        text: contentReverse
      }
    }, {
      background: semanticError,
      border: semanticError,
      text: contentReverse,
      hover: {
        background: semanticErrorBorderHover,
        border: semanticErrorBorderHover,
        text: contentReverse
      }
    }],
    secondary: [{
      background: backgroundPrimary,
      border: $isFilter ? borderStrong : borderColor,
      text: content,
      hover: {
        background: backgroundPrimary,
        border: $isFilter ? borderStrongHover : borderHover,
        text: content
      }
    }, {
      background: backgroundPrimary,
      border: semanticError,
      text: semanticError,
      hover: {
        background: backgroundPrimary,
        border: semanticErrorBorderHover,
        text: semanticErrorBorderHover
      }
    }],
    tertiary: [{
      background: 'none',
      border: 'none',
      text: actionablePrimary,
      hover: {
        background: 'none',
        border: 'none',
        text: actionableSecondary
      }
    }, {
      background: 'none',
      border: 'none',
      text: semanticError,
      hover: {
        background: 'none',
        border: 'none',
        text: semanticErrorBorderHover
      }
    }]
  };
  const {
    text,
    border,
    background,
    hover = {}
  } = colors?.[$emphasis]?.[Number($destructive ?? false)] || {};
  return css`
    color: ${text};
    border: ${$isFilter ? '1px' : '2px'} solid ${border};
    background-color: ${background};

    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;

    @media (hover: hover) {
      &:hover {
        background-color: ${disabled ? background : hover.background};
        border-color: ${disabled ? border : hover.border};
      }
    }

    &:hover {
      color: ${disabled ? text : hover.text};
      text-decoration: none;
    }

    &[aria-expanded='true'],
    &:focus-visible {
      border-color: ${disabled ? border : $isFilter ? semanticInfoStrong : hover.border};
      ${$isFilter ? `outline: 1px solid ${semanticInfoStrong};` : undefined}
    }

    &:active,
    &[aria-expanded='true'],
    &:focus-visible {
      text-decoration: none;
      color: ${disabled ? text : hover.text};
      background-color: ${disabled ? background : hover.background};
    }

    /* prevent to render the outline behind another near element */
    &:focus-visible {
      position: relative;
      z-index: 1;
    }
    ${focusVisibleWithOutline({
    offset: 'default',
    radius: theme.btnBorderRadius
  })}
  `;
};
const getDisabledStyles = ({
  disabled,
  $loading
}) => disabled ? css`
        opacity: ${$loading ? 1 : 0.4};
        cursor: ${$loading ? 'wait' : 'not-allowed'};
      ` : '';
export const buttonSizeProps = {
  [Sizes.xsmall]: {
    fontSize: 'btnXSmallFontSize',
    lineHeight: 'btnXSmallLineHeight',
    padding: 'btnXSmallPadding'
  },
  [Sizes.small]: {
    fontSize: 'btnSmallFontSize',
    lineHeight: 'btnSmallLineHeight',
    padding: 'btnSmallPadding'
  },
  [Sizes.normal]: {
    fontSize: 'btnFontSize',
    lineHeight: 'btnLineHeight',
    padding: 'btnPadding'
  },
  [Sizes.large]: {
    fontSize: 'btnLargeFontSize',
    lineHeight: 'btnLargeLineHeight',
    padding: 'btnLargePadding'
  }
};
const Button = styled.button`
  display: inline-block;
  position: relative;
  padding: ${({
  $isFilter,
  $emphasis,
  'data-size': size,
  theme
}) => $isFilter ? 'calc(0.5rem - 1px) 1rem' : $emphasis === tertiary ? '0' : theme[buttonSizeProps[size].padding]};

  /**
   * For text links: The border-radius mess with the underline
   */
  border-radius: ${({
  $isTextLink,
  $isFilter,
  theme
}) => $isTextLink ? 'unset' : $isFilter ? theme.textInputBorderRadius : theme.btnBorderRadius};

  &[data-full-width='true'] {
    width: 100%;
    display: inline-flex;
    > span.contentWrapper {
      display: flex;
      width: 100%;
      justify-content: ${({
  $isFilter,
  $justifyBetween
}) => $isFilter || $justifyBetween ? 'space-between' : 'center'};
    }
  }

  font-size: ${({
  'data-size': size,
  theme
}) => theme[buttonSizeProps[size].fontSize]};
  font-weight: ${props => props.theme.btnFontWeight};
  line-height: ${({
  'data-size': size,
  theme
}) => theme[buttonSizeProps[size].lineHeight]};
  text-transform: ${props => props.$textTransform || 'capitalize'};
  text-align: center;
  text-decoration: none;

  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  white-space: nowrap;

  &:focus {
    outline: 0;
    box-shadow: ${({
  $emphasis,
  theme,
  $isFilter
}) => $emphasis === tertiary || $isFilter ? 'none' : theme.boxShadowMedium};
  }

  /**
   * For text links: Remove the underline when focused (:focus-visible)
   * This prevents the underline from appearing with curved edges
   * when the focus indicator ring is displayed.
   */
  ${({
  $isTextLink
}) => $isTextLink ? `&:focus-visible {
      border-bottom: none;
  }` : ''}

  ${StyledMaterialIcon} {
    vertical-align: bottom;
    margin: ${({
  $alignIcon
}) => $alignIcon === 'start' ? '0 0.5ch 0 0' : '0 0 0 0.5ch'};
  }

  > span.contentWrapper {
    transition: opacity 0.2s linear;
    opacity: ${({
  $loading
}) => $loading ? '0' : '1'};
  }

  ${getButtonStyles}
  ${getDisabledStyles}
`;
export default Button;
//# sourceMappingURL=styles.js.map